import React, { useCallback, useEffect, useState } from "react";

import { getFieldShapesAdmin, getUserFarms, verifyFarm } from "services";

import { useCancelAsyncOnUnmount, useNavigate } from "utils";

import {
  AdminDescriptions,
  Card,
  Col,
  ErrorBoundary,
  Flex,
  Form,
  Icon,
  InsightCard,
  MapInteractive,
  Option,
  Row,
  Select,
  Skeleton,
  Spin,
  Typography,
  Message as message,
} from "components";

import { FarmSizeForm } from "pages/admin/verifications/components/forms/FarmSizeForm";
import { VerificationOption } from "pages/admin/verifications/components/forms/VerificationOption";
import { VerificationComments } from "pages/admin/verifications/components/misc/VerificationComments";

export const FarmOverview = ({ farm, farmId, onUpdate }) => {
  const [geometries, setGeometries] = useState([]);
  const [commentCount, setCommentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [relatedFarms, setRelatedFarms] = useState([]);

  // const { year: currentYear } = useCurrentYearlyDataYear(farmId);
  const signal = useCancelAsyncOnUnmount()();
  const navigate = useNavigate();

  const loadUserFarms = useCallback(() => {
    if (farm?.userId) {
      getUserFarms({
        userId: farm.userId,
        signal,
      }).then((res) => setRelatedFarms(res));
    }
  }, [signal, farm?.userId]);

  const loadGeometries = useCallback(() => {
    setLoading(true);
    getFieldShapesAdmin({
      farmId,
      signal,
    })
      .then((res) => setGeometries(res.fieldShapes))
      .finally(() => setLoading(false));
  }, [farmId, signal]);

  const handleVerifyFarm = (e) => {
    verifyFarm({
      signal,
      id: farmId,
      verified: e,
    }).then((res) => {
      if (res.success) {
        onUpdate();
        message({
          content: "Verification successful",
          icon: "gremlin",
        });
      } else {
        message({
          content: "There was a problem updating the verification",
          icon: "gremlin",
        });
      }
    });
  };

  useEffect(() => {
    loadGeometries();
  }, [loadGeometries]);

  useEffect(() => {
    loadUserFarms();
  }, [loadUserFarms]);

  if (loading || !farm) {
    return <Spin />;
  }

  const { addressCity, addressNumber, addressStreet, region, size, verified, zip } = farm;
  const relevantRelatedFarms = relatedFarms.filter((item) => !item.farm.disabled).filter((relatedFarm) => farm.id !== relatedFarm.farmId);

  return (
    <>
      <ErrorBoundary>
        {geometries.length > 0 && (
          <Row className="margin-top-200">
            <Col>
              <Card className="map-card">
                <MapInteractive
                  geometries={geometries}
                  location={geometries[0]?.center}
                  markers={geometries.map(({ center }) => ({ location: center }))}
                  scaleControl={true}
                  zoomControl={true}
                />
              </Card>
            </Col>
          </Row>
        )}
      </ErrorBoundary>
      <ErrorBoundary>
        <Row>
          <Col sm={8} md={8}>
            {verified || verified === false ? (
              <InsightCard label={verified ? "approved" : "rejected"} status={verified ? "success" : "alert"}>
                1
              </InsightCard>
            ) : (
              <InsightCard className="yellow" label="completed verifications">
                0/1
              </InsightCard>
            )}
          </Col>
        </Row>
      </ErrorBoundary>
      <ErrorBoundary>
        <Row className="margin-top-100">
          <Col>
            {farm ? (
              <AdminDescriptions
                items={[
                  {
                    label: (
                      <Flex vertical>
                        <Typography variant="paragraph-m">Farm size</Typography>
                        <Typography variant="paragraph-xs">total size including non-Klim managed fields</Typography>
                      </Flex>
                    ),
                    span: 1,
                    children: <FarmSizeForm farmId={farmId} size={size} onSuccess={onUpdate} />,
                  },
                  {
                    label: "Region",
                    span: 1,
                    children: region || "Unknown",
                  },
                  {
                    label: "Address",
                    span: 1,
                    children:
                      addressStreet || addressNumber ? (
                        <>
                          {addressStreet} {addressNumber}
                          <br />
                          {zip} {addressCity}
                        </>
                      ) : (
                        "Unknown"
                      ),
                  },
                  {
                    label: "Related farms",
                    span: 3,
                    children: relevantRelatedFarms.length ? (
                      relevantRelatedFarms.map((item, i) => (
                        <Typography
                          variant="paragraph-m"
                          bold
                          key={i}
                          onClick={() => navigate(`/verifications/farms/${item.farmId}/farm`)}
                          color="green-400"
                          className="link"
                        >
                          {`${item.farm.name}${i === relevantRelatedFarms.length - 1 ? "" : ","} `}
                        </Typography>
                      ))
                    ) : (
                      <Typography variant="paragraph-m">User has only one farm</Typography>
                    ),
                  },
                  {
                    label: "Verification",
                    span: 1.5,
                    children: (
                      <Form>
                        <Select name="verified" initialValue={`${verified}`} onChange={(e) => handleVerifyFarm(e)} className="verification-select">
                          <Option value="true">
                            <VerificationOption label="approved" iconName="tabler/circle-check" className="approved" />
                          </Option>
                          <Option value="false">
                            <VerificationOption label="rejected" iconName="tabler/circle-x" className="rejected" />
                          </Option>
                          <Option value="null">
                            <VerificationOption label="not verified" iconName="tabler/help" />
                          </Option>
                        </Select>
                      </Form>
                    ),
                  },
                  {
                    label: "Comments",
                    span: 1.5,
                    children: (
                      <>
                        <Icon iconName={"tabler/message-2"} className="icon inline" />
                        {commentCount}
                      </>
                    ),
                  },
                ]}
              />
            ) : (
              <Skeleton type="paragraph" rows={3} />
            )}
          </Col>
        </Row>

        <Row>
          <Col>
            <Typography variant="h2" className="margin-bottom-200">
              Comments
            </Typography>
            <VerificationComments farmId={farmId} type={"farm"} setCommentCount={setCommentCount} />
          </Col>
        </Row>
      </ErrorBoundary>
    </>
  );
};
